// TEXT INSERT PANEL — the left "Text" drawer, rebuilt 2026-07-22 (owner:
// "The insert Text drawer is horrible. We need sections and icon blocks
// instead of tabs. Icons like app icons for header/subheader/paragraph, a
// Decorative section with ~12 styles (absorbing the old Special tab), and a
// 3D section for 3D text").
//
// ONE scrolling column, THREE labelled sections, NO tabs:
//   ① ADD TEXT   — app-icon tiles (Header / Subheader / Paragraph / Caption).
//                  Click = addText at a workspace-relative size (single verb =
//                  one undo entry). Folds in the old size cards + QUICK ADD row.
//   ② DECORATIVE — a 2-col grid of 12 curated manifest styles rendered as TRUE
//                  previews (stylePreviewRenderer — the exact swatch the
//                  sidebar featured tiles use, so a tile can't drift from what
//                  a click paints). Click = insert "Your text" + applyTextStyle.
//                  Absorbs the dead legacy TEXT EFFECTS chips (their looks live
//                  on here as real recipes) and the old Special tab.
//   ③ 3D TEXT    — tiles that insert text + patchTextFx('extrude', …) on the
//                  LIVE editable text. Faces are the DepthTileFace styled-`Aa`
//                  idiom — NO 3D bakes in the panel (boot/verify law).
//
// House machinery reused verbatim: editor.addText (hug-law width, workspace-%
// default size), editor.applyTextStyle({id,label,recipe}), editor.patchTextFx,
// editor.batch (insert+style coalesced to ONE undo entry — see the facade note
// on batch), renderStyleSwatch, the 273-style curated manifest, DepthTileFace.
// The drawer is handed the INSERT-latched editor by StudioCoreEditor
// (insertEditor prop pattern), so every addText auto-closes the drawer.
import React, { useEffect, useState } from 'react';
import { loadEditorFonts } from '../../editorFonts';
import { renderStyleSwatch } from './stylePreviewRenderer';
import styleManifest from './styles/styleManifest.json';
import DepthTileFace from './depthTileFaces';

// ── DECORATIVE: named CATEGORIES of curated manifest styles (owner 2026-07-23:
// "we need some more styles and style categories"). Each category picks
// visually distinct recipes from a look-family (the manifest has 273 styles
// across ~55 families); ids are the manifest's own (persistence contract).
// The tile label is the recipe's own manifest label (short enough already).
const DECORATIVE_CATS = [
  {
    name: 'Popular',
    ids: ['comic-pop', 'shadow-pop', 's-gradient-pop-sunset-pop', 's-neon-tube-chalk-neon',
      's-tex-marble-gold', 'sticker', 'outline', 's-two-tone-sunset-pop'],
  },
  {
    name: 'Sports',
    ids: ['s-varsity-team-gold-navy', 's-pennant-team-gold-navy', 's-jersey-primary-brights',
      's-scoreboard-team-gold-navy', 's-team-split-team-gold-navy', 's-label-block-primary-brights',
      's-big-sticker-primary-brights', 's-harvest-block-team-gold-navy'],
  },
  {
    name: 'Fun & Party',
    ids: ['s-comic-burst-primary-brights', 's-tex-confetti-party', 's-party-pop-primary-brights',
      's-candy-stripe-bubblegum', 's-star-sticker-primary-brights', 's-petal-pop-springtime',
      's-rainbow-pop-primary-brights', 's-sticky-note-primary-brights'],
  },
  {
    name: 'Glow & Neon',
    ids: ['neon-sign', 's-led-bright-chalk-neon', 's-cyber-glow-chalk-neon', 's-ghost-glow-chalk-neon',
      's-spooky-glow-berry', 's-ember-glow-berry', 's-cosmic-ocean', 's-spotlight-ocean'],
  },
  {
    name: 'Elegant',
    ids: ['s-foil-shine-team-gold-navy', 's-chrome-grayscale-pro', 's-tex-gold-foil-luxe',
      's-frosted-winter-frost', 's-soft-lift-ocean', 's-retro-print-grayscale-pro', 's-fade-berry', 'gold-foil'],
  },
  {
    name: 'Outline',
    ids: ['outline', 's-bold-outline-primary-brights', 's-open-outline-ocean', 's-fine-outline-berry',
      'hollow', 's-hollow-pop-primary-brights', 's-outline-pop-ocean', 's-marker-ocean'],
  },
];

const STYLE_BY_ID = new Map(styleManifest.styles.map((s) => [s.id, s]));
// Resolve each category's ids → style objects (drop any that ever go missing).
const DECORATIVE_GROUPS = DECORATIVE_CATS.map((c) => ({
  name: c.name,
  styles: c.ids.map((id) => STYLE_BY_ID.get(id)).filter(Boolean),
}));
// Flat list for the eager swatch render pass.
const DECORATIVE = DECORATIVE_GROUPS.flatMap((g) => g.styles);

const SWATCH_PX = 72; // device px; the ~52px tile art downscales it crisp.

// ── 3D: extrude presets applied to LIVE text via patchTextFx('extrude', …) ──
// depth 55 = FX_EXTRUDE_DEFAULTS.depth (world ~34); volumeRound 70 for the
// inflated styles ('solid' omits volumeStyle by law). Only presets that LOOK
// different ship. NOT the three.js TEXT3D_PRESETS — those are the heavy baked
// panel; these run on real editable text.
const THREE_D = [
  { id: 'solid', label: 'Solid 3D', face: 'extrude', fx: { depth: 55 } },
  { id: 'puffy', label: 'Puffy', face: 'puffy', fx: { depth: 55, volumeStyle: 'puffy', volumeRound: 70 } },
  { id: 'balloon', label: 'Balloon', face: 'balloon', fx: { depth: 55, volumeStyle: 'pillow', volumeRound: 70 } },
  { id: 'balloon-round', label: 'Balloon Round', face: 'balloon', fx: { depth: 55, volumeStyle: 'pillow', volumeRound: 70, volumeBack: 'round' } },
];

// Styled-`Aa` face looks for the 3D tiles (the DepthTileFace idiom — pure CSS,
// zero bakes). 'solid' reuses the canonical extrude face; puffy/balloon are
// siblings in the same cool palette so the row reads as one family.
const FACE_BASE = {
  display: 'inline-block',
  fontFamily: 'Lexend, sans-serif',
  fontWeight: 800,
  lineHeight: 1,
  letterSpacing: '-0.02em',
};
const PUFFY_LOOK = {
  color: '#f1eefe',
  textShadow: ['0 0.05em 0 #c3b5f5', '0 0.1em 0 #a992ef', '0 0.16em 0.16em rgba(60,45,120,0.5)'].join(', '),
};
const BALLOON_LOOK = {
  color: '#eef2ff',
  textShadow: ['0 -0.02em 0 #ffffff', '0 0.06em 0 #b9c6f6', '0 0.13em 0 #93a7f2', '0 0.2em 0.18em rgba(50,45,110,0.5)'].join(', '),
};

function Face3D({ kind }) {
  if (kind === 'extrude') return <DepthTileFace kind="extrude" className="cce-ti-3d-face" />;
  const look = kind === 'puffy' ? PUFFY_LOOK : BALLOON_LOOK;
  return <span className="cce-ti-3d-face" style={{ ...FACE_BASE, ...look }} aria-hidden="true">Aa</span>;
}

export default function TextInsertPanel({ editor, isOpen, onClose }) {
  const [thumbs, setThumbs] = useState({}); // styleId → blob URL (LRU-owned)

  // Preset sizes are fractions of the artboard width so text lands at poster
  // scale (a fixed px default is unreadably small on a 1728px+ canvas).
  const W = editor?.getWidth?.() || 1728;
  const px = (frac) => Math.round(W * frac);

  // TRUE previews for the 12 decorative tiles — same renderer/contract as the
  // sidebar featured swatches (short "Aa", transparent PNG, LRU-cached). Ground
  // follows the manifest's groundHint (neon/chalk pin dark); the tile art gives
  // each a matching backdrop. Rendered eagerly while the drawer is open (12
  // swatches ≈ a few ms each, cached) — no IntersectionObserver needed at this
  // count.
  useEffect(() => {
    if (!isOpen) return undefined;
    let alive = true;
    DECORATIVE.forEach((s) => {
      renderStyleSwatch({
        recipe: s.recipe,
        fontFamily: 'Lexend',
        fontWeight: 700,
        ground: s.groundHint === 'dark' ? 'dark' : 'light',
        size: SWATCH_PX,
        cacheKey: `ti-deco|${s.id}`,
      })
        .then((url) => { if (alive) setThumbs((m) => ({ ...m, [s.id]: url })); })
        .catch(() => { /* tile keeps its Aa placeholder */ });
    });
    return () => { alive = false; };
  }, [isOpen]);

  if (!isOpen) return null;

  // ADD TEXT tiles — Header ≈ current title size, Subheader ≈ 0.6×, Paragraph
  // ≈ 0.35× with a starter sentence, Caption ≈ 0.28× italic (the old size
  // cards folded in). Every bundled font (editorFonts.js) so preview == canvas.
  const addTiles = [
    { id: 'header', label: 'Header', role: 'header', text: 'Header', opts: { fontSize: px(0.10), fontWeight: 700, fontFamily: 'Lexend' } },
    { id: 'subheader', label: 'Subheader', role: 'subheader', text: 'Subheader', opts: { fontSize: px(0.06), fontWeight: 600, fontFamily: 'Lexend' } },
    { id: 'paragraph', label: 'Paragraph', role: 'paragraph', text: 'Type your paragraph text here.', opts: { fontSize: px(0.035), fontWeight: 400, fontFamily: 'Nunito' } },
    { id: 'caption', label: 'Caption', role: 'caption', text: 'Caption text', opts: { fontSize: px(0.028), fontWeight: 400, fontFamily: 'Nunito', fontStyle: 'italic' } },
  ];

  // Single verb = one undo entry on its own; the insert-latched editor closes
  // the drawer on the object:added that follows.
  const insertBasic = async (tile) => {
    if (!editor) return;
    await loadEditorFonts();
    editor.addText(tile.text, tile.opts);
  };

  // Insert "Your text" at header-ish size, then paint the style — coalesced to
  // ONE undo entry via editor.batch (applyTextStyle self-batches; batch.end's
  // save() dedupes, so the outer wrap collapses to a single entry). The label
  // sent is the CANONICAL manifest label (persistence), not the short tile one.
  const insertDecorative = async (s) => {
    if (!editor) return;
    await loadEditorFonts();
    editor.batch?.begin?.();
    try {
      editor.addText('Your text', { fontSize: px(0.09), fontWeight: 700, fontFamily: 'Lexend' });
      await editor.applyTextStyle({ id: s.id, label: s.label, recipe: s.recipe });
    } catch (err) {
      console.error('Decorative text insert failed:', err);
    } finally {
      editor.batch?.end?.();
    }
  };

  // Insert text + turn on live 3D — patchTextFx doesn't self-commit, so the
  // batch owns the single entry cleanly.
  const insert3D = async (preset) => {
    if (!editor) return;
    await loadEditorFonts();
    editor.batch?.begin?.();
    try {
      editor.addText('Your text', { fontSize: px(0.10), fontWeight: 800, fontFamily: 'Lexend' });
      editor.patchTextFx('extrude', preset.fx);
    } catch (err) {
      console.error('3D text insert failed:', err);
    } finally {
      editor.batch?.end?.();
    }
  };

  const renderAddArt = (role) => {
    if (role === 'paragraph') {
      return (
        <div className="cce-ti-para" aria-hidden="true">
          <span /><span /><span /><span />
        </div>
      );
    }
    return <span className={`cce-ti-aa ${role}`} aria-hidden="true">Aa</span>;
  };

  return (
    <div className="cce-tool-sidebar">
      <div className="cce-tool-header">
        <h3>Text</h3>
        <button onClick={onClose} className="cce-close-btn" aria-label="Close text panel">×</button>
      </div>

      {/* ① ADD TEXT — app-icon tiles */}
      <div className="cce-ti-section">
        <p className="cce-ti-section-label">Add Text</p>
        <div className="cce-ti-grid cce-ti-grid-4">
          {addTiles.map((tile) => (
            <button
              key={tile.id}
              type="button"
              className="cce-ti-tile"
              title={tile.label}
              onClick={() => insertBasic(tile)}
            >
              <span className="cce-ti-tile-art plain">{renderAddArt(tile.role)}</span>
              <span className="cce-ti-tile-label">{tile.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ② DECORATIVE — named categories of curated manifest styles */}
      <div className="cce-ti-section">
        <p className="cce-ti-section-label">Decorative</p>
        {DECORATIVE_GROUPS.map((g) => (
          <div key={g.name} className="cce-ti-cat">
            <p className="cce-ti-cat-label">{g.name}</p>
            <div className="cce-ti-grid cce-ti-grid-4">
              {g.styles.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className="cce-ti-tile cce-ti-tile-sm"
                  title={s.label}
                  onClick={() => insertDecorative(s)}
                >
                  <span className={`cce-ti-tile-art ${s.groundHint === 'dark' ? 'dark' : 'light'}`}>
                    {thumbs[s.id]
                      ? <img className="cce-ti-tile-img" src={thumbs[s.id]} alt="" draggable={false} />
                      : <span className="cce-ti-tile-aa-fallback">Aa</span>}
                  </span>
                  <span className="cce-ti-tile-label">{s.label}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* ③ 3D TEXT — live extrude presets, styled-Aa faces */}
      <div className="cce-ti-section">
        <p className="cce-ti-section-label">3D Text</p>
        <div className="cce-ti-grid cce-ti-grid-4">
          {THREE_D.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className="cce-ti-tile"
              title={preset.label}
              onClick={() => insert3D(preset)}
            >
              <span className="cce-ti-tile-art dark">
                <Face3D kind={preset.face} />
              </span>
              <span className="cce-ti-tile-label">{preset.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
